  import React, { useEffect, useRef } from "react";
  import { useDispatch, useSelector } from "react-redux";
  import { toast } from "react-toastify";
  import clock from "../assets/images/wall-clock.png";
  import {
    clockInEmployee,
    clockOutEmployee,
    getAttendanceDashboard,
    getStatusClock,
  } from "../redux/dashboard/dashboardActions";
  import { selectAttendanceDashboardData } from "../redux/dashboard/dashboardSelectors";
  import { updateUserWorkStatusApis } from "../redux/users/userActions";
  import { selectClockStatus, selectClockStatusLoading } from "../redux/dashboard/dashboardSelectors";
  import { getCurrentUser } from "../utils/common";

  /**
   * Compact clock in / out button for the top header.
   * Self-contained: reads the authoritative clock state from redux
   * (attendanceData.IsClockedIn) and dispatches the same thunks the dashboard
   * card uses, so both stay in sync. Shown on mobile only (see header.css).
   */
  const HeaderClockButton = () => {
    const dispatch = useDispatch();
    const currentUser = getCurrentUser();
    // const attendanceData = useSelector(selectAttendanceDashboardData);
    // const isClockedIn = !!attendanceData?.IsClockedIn;

    const clockStatus = useSelector(selectClockStatus);

    const clockStatusLoading = useSelector(selectClockStatusLoading);
    const isClockedIn = clockStatus?.toLowerCase() === "clocked in"
    const busyRef = useRef(false);

    /* The current attendance state (the backend resolves the user from the
       token), kept fresh rather than read once.

       Once-on-mount was not enough. The work-status menu refetches this after
       a status change, but nothing does after a TASK moves the clock, and
       nothing does when the change came from another tab. The readout then
       sits on whatever it learned at mount, and a stale readout is
       indistinguishable from a broken clock to the person reading it — which
       is exactly the complaint this control keeps attracting.

       A minute is enough: every path that moves the clock is a deliberate act
       whose own screen already refreshes, so this is the backstop, not the
       mechanism. Refetched on tab focus too, because coming back to a tab left
       open since this morning is when it is most likely to be wrong. */
    useEffect(() => {
      if (!currentUser?.userId) return undefined;

      const refresh = () => dispatch(getStatusClock());

      refresh();
      const id = setInterval(refresh, 60000);
      const onFocus = () => {
        if (document.visibilityState === "visible") refresh();
      };
      document.addEventListener("visibilitychange", onFocus);

      return () => {
        clearInterval(id);
        document.removeEventListener("visibilitychange", onFocus);
      };
    }, [dispatch, currentUser?.userId]);

    const handleClick = async () => {
      if (busyRef.current || clockStatusLoading || !currentUser?.userId) return; // ignore double taps
      busyRef.current = true;
      try {
        if (isClockedIn) {
          const res = await dispatch(
            clockOutEmployee(currentUser.userId)
          ).unwrap();
          toast.success(res?.data?.message || "Clock Out Successful");
        } else {
          const res = await dispatch(
            clockInEmployee(currentUser.userId)
          ).unwrap();
          toast.success(res?.data?.message || "Clock In Successful");
        }
        const statusPayload = {
          id: currentUser.userId,
          workStatus: isClockedIn 
          ? "Inacitve"
          : "Active",
        };

        console.log("Initial work status:", statusPayload);
        
        await dispatch(updateUserWorkStatusApis(statusPayload)).unwrap()
        await dispatch(getStatusClock()).unwrap();

        dispatch(getAttendanceDashboard());
      } catch (err) {
        console.error("Clock action error:", err);
        toast.error(err?.message || err || "Action failed");
      } finally {
        busyRef.current = false;
      }
    };

    /* Display only.

       The clock is driven entirely by what the person actually does now:
       signing in punches IN, choosing Break / Lunch / Be right back punches
       OUT (and pauses the running task), choosing Active punches back IN, and
       starting a task punches IN if they forgot. With every one of those
       covered, a button that lets somebody clock out by hand while still
       working — or clock in while at lunch — can only put the record out of
       step with the truth.

       Kept visible rather than removed: it is the clearest read of "am I on
       the clock right now", which is worth a permanent place in the header.
       handleClick is left in the file so the button can be made live again
       without rebuilding it. */
    /* "Clock In" / "Clock Out" — the wording asked for.
     *
     * These read as commands rather than as a state, which is the one risk in
     * them: "Clock In" is shown while you ARE clocked in, so it can be taken
     * as an offer to clock in rather than a report that you already are. The
     * colour carries the state where the words do not — green for on, red for
     * off — and the tooltip says it in full. */
    const label = clockStatusLoading
      ? "…"
      : isClockedIn
        ? "Clock In"
        : "Clock Out";

    return (
      <span
        className={`header-clock-btn header-clock-btn--static ${
          isClockedIn ? "header-clock-btn--in" : "header-clock-btn--off"
        }`}
        title={`${isClockedIn ? "Clocked in" : "Clocked out"} — set automatically by signing in, by your work status, and by starting a task. This is a status, not a button.`}
        aria-live="polite"
      >
        <img src={clock} alt="" />
        {label}
      </span>
    );
  };

  export default HeaderClockButton;